<script>
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import PrincipalHeader from "@/views/pages/dashboard/principal/components/Principal-header.vue";
import PrincipalSidebar from "@/views/pages/dashboard/principal/components/Principal-sidebar.vue";
import { useSharedStore } from "@/stores/shared-store";
import { useLearningPathStore } from "@/stores/learning-path-store";
import { useUserStore } from "@/stores/user/user-store";

export default {
  name: "PrincipalLearningPath",
  components: {
    BaseLoading,
    PrincipalHeader,
    PrincipalSidebar,
  },

  data() {
    return {
      sharedStore: useSharedStore(),
      learningPathStore: useLearningPathStore(),
      userStore: useUserStore(),

      searchQuery: "",
      selectedTeacherId: "",
      teacherSearchQuery: "",
      isTeacherDropdownOpen: false,

      // Detail Modal
      showDetailModal: false,
      selectedPath: null,
    };
  },

  computed: {
    // List guru aktif untuk dropdown filter
    teacherList() {
      return (this.userStore.ActiveTeachers || []).filter(
        (t) => !t.deleted_at
      );
    },

    // Opsi guru yang sudah difilter berdasarkan ketikan autocomplete
    filteredTeacherOptions() {
      const q = this.teacherSearchQuery.toLowerCase().trim();
      if (!q) return this.teacherList;
      return this.teacherList.filter((t) =>
        t.name?.toLowerCase().includes(q)
      );
    },

    // Filter paths berdasarkan search dan guru
    filteredPaths() {
      let paths = this.learningPathStore.learningPaths || [];

      // Filter by teacher
      if (this.selectedTeacherId) {
        paths = paths.filter(
          (p) => String(p.teacher) === String(this.selectedTeacherId)
        );
      }

      // Filter by query (title, note, teacher_name)
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        paths = paths.filter(
          (p) =>
            p.title?.toLowerCase().includes(q) ||
            p.note?.toLowerCase().includes(q) ||
            p.teacher_name?.toLowerCase().includes(q)
        );
      }

      return paths;
    },

    // Statistik Ringkasan
    totalPaths() {
      return this.learningPathStore.learningPaths.length;
    },
    totalCourses() {
      return this.learningPathStore.learningPaths.reduce(
        (acc, curr) => acc + (curr.item_count || 0),
        0
      );
    },
    totalStudentsAssigned() {
      return this.learningPathStore.learningPaths.reduce(
        (acc, curr) => acc + (curr.student_count || 0),
        0
      );
    },
    uniqueTeachersCount() {
      const teacherIds = new Set(
        this.learningPathStore.learningPaths
          .map((p) => p.teacher)
          .filter(Boolean)
      );
      return teacherIds.size;
    },
  },

  async mounted() {
    await this.fetchData();
    document.addEventListener("click", this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  methods: {
    async fetchData() {
      await Promise.all([
        this.learningPathStore.fetchAllPaths(),
        this.userStore.fetchTeachers(),
      ]);
    },

    handleClickOutside(event) {
      if (
        this.$refs.teacherAutocomplete &&
        !this.$refs.teacherAutocomplete.contains(event.target)
      ) {
        this.isTeacherDropdownOpen = false;
        // Jika belum ada guru yang dipilih tapi input ada teksnya, kembalikan ke teks kosong
        if (!this.selectedTeacherId) {
          this.teacherSearchQuery = "";
        } else {
          // Jika sudah ada guru yang dipilih, pastikan teksnya sesuai nama guru yang dipilih
          const found = this.teacherList.find(
            (t) => String(t.userID) === String(this.selectedTeacherId)
          );
          if (found) this.teacherSearchQuery = found.name;
        }
      }
    },

    selectTeacher(teacher) {
      if (!teacher) {
        this.selectedTeacherId = "";
        this.teacherSearchQuery = "";
      } else {
        this.selectedTeacherId = teacher.userID;
        this.teacherSearchQuery = teacher.name;
      }
      this.isTeacherDropdownOpen = false;
    },

    clearTeacherFilter() {
      this.selectedTeacherId = "";
      this.teacherSearchQuery = "";
      this.isTeacherDropdownOpen = false;
    },

    onTeacherInput() {
      this.isTeacherDropdownOpen = true;
      if (!this.teacherSearchQuery.trim()) {
        this.selectedTeacherId = "";
      }
    },

    openTeacherDropdown() {
      this.isTeacherDropdownOpen = true;
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("id-ID", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    resetFilters() {
      this.searchQuery = "";
      this.clearTeacherFilter();
    },

    openDetail(path) {
      this.selectedPath = path;
      this.showDetailModal = true;
    },

    closeDetail() {
      this.showDetailModal = false;
      this.selectedPath = null;
    },
  },
};
</script>

<template>
  <layouts-header></layouts-header>

  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Learning Path</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item">
                <router-link to="/principal/dashboard">Principal</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Learning Path
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <div class="content">
    <div class="container">
      <PrincipalHeader></PrincipalHeader>
      <div class="row">
        <PrincipalSidebar></PrincipalSidebar>

        <div class="col-lg-9">
          <!-- Page Title & Header -->
          <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <div>
              <h5 class="fw-bold mb-1">Daftar Semua Learning Path</h5>
              <p class="text-muted fs-14 mb-0">
                Pantau seluruh alur pembelajaran (Learning Path) yang telah dibuat oleh para Guru beserta alokasi siswa.
              </p>
            </div>
            <button
              class="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
              @click="fetchData"
              title="Refresh Data"
            >
              <i class="fa-solid fa-rotate-right me-1"></i> Refresh
            </button>
          </div>

          <!-- Summary Cards -->
          <div class="row g-3 mb-4">
            <div class="col-md-3 col-sm-6">
              <div class="card border-0 shadow-sm rounded-3 h-100">
                <div class="card-body d-flex align-items-center p-3">
                  <div
                    class="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style="width: 48px; height: 48px; background-color: #eef2ff;"
                  >
                    <img src="@/assets/img/icon/career-01.svg" alt="Total Path" width="24" height="24" />
                  </div>
                  <div>
                    <h6 class="text-muted fs-12 mb-1">Total Path</h6>
                    <h4 class="fw-bold mb-0 text-dark">{{ totalPaths }}</h4>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-3 col-sm-6">
              <div class="card border-0 shadow-sm rounded-3 h-100">
                <div class="card-body d-flex align-items-center p-3">
                  <div
                    class="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style="width: 48px; height: 48px; background-color: #e0f2fe;"
                  >
                    <img src="@/assets/img/icon/book.svg" alt="Total Course" width="24" height="24" />
                  </div>
                  <div>
                    <h6 class="text-muted fs-12 mb-1">Total Course Terkait</h6>
                    <h4 class="fw-bold mb-0 text-dark">{{ totalCourses }}</h4>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-3 col-sm-6">
              <div class="card border-0 shadow-sm rounded-3 h-100">
                <div class="card-body d-flex align-items-center p-3">
                  <div
                    class="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style="width: 48px; height: 48px; background-color: #f0fdf4;"
                  >
                    <img src="@/assets/img/icon/student.svg" alt="Total Siswa" width="24" height="24" />
                  </div>
                  <div>
                    <h6 class="text-muted fs-12 mb-1">Total Siswa Terdaftar</h6>
                    <h4 class="fw-bold mb-0 text-dark">{{ totalStudentsAssigned }}</h4>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-3 col-sm-6">
              <div class="card border-0 shadow-sm rounded-3 h-100">
                <div class="card-body d-flex align-items-center p-3">
                  <div
                    class="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style="width: 48px; height: 48px; background-color: #fef3c7;"
                  >
                    <img src="@/assets/img/icon/teacher.svg" alt="Guru Pembuat" width="24" height="24" />
                  </div>
                  <div>
                    <h6 class="text-muted fs-12 mb-1">Guru Pembuat</h6>
                    <h4 class="fw-bold mb-0 text-dark">{{ uniqueTeachersCount }}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Search & Filter Bar -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-body p-3">
              <div class="row g-2 align-items-center">
                <!-- Search Input -->
                <div class="col-md-6 col-12">
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0">
                      <img src="@/assets/img/icon/search-icon.svg" alt="Search" width="16" height="16" />
                    </span>
                    <input
                      type="text"
                      class="form-control border-start-0 ps-0"
                      placeholder="Cari judul path, deskripsi, atau guru..."
                      v-model="searchQuery"
                    />
                  </div>
                </div>

                <!-- Teacher Autocomplete Filter -->
                <div class="col-md-4 col-sm-8 col-12 position-relative" ref="teacherAutocomplete">
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0">
                      <img src="@/assets/img/icon/teacher.svg" alt="Teacher" width="16" height="16" />
                    </span>
                    <input
                      type="text"
                      class="form-control border-start-0 border-end-0 ps-0"
                      placeholder="Cari guru pembuat..."
                      v-model="teacherSearchQuery"
                      @input="onTeacherInput"
                      @focus="openTeacherDropdown"
                    />
                    <!-- Clear button jika ada teks atau guru terpilih -->
                    <button
                      v-if="selectedTeacherId || teacherSearchQuery"
                      class="btn btn-light border border-start-0 text-muted px-2"
                      type="button"
                      @click="clearTeacherFilter"
                      title="Hapus filter guru"
                    >
                      <img src="@/assets/img/icon/close-circle.svg" alt="Clear" width="14" height="14" />
                    </button>
                    <!-- Tombol toggle dropdown -->
                    <button
                      class="btn btn-light border border-start-0 text-muted px-2"
                      type="button"
                      @click="isTeacherDropdownOpen = !isTeacherDropdownOpen"
                      title="Buka daftar guru"
                    >
                      <i class="fa-solid fa-chevron-down fs-12"></i>
                    </button>
                  </div>

                  <!-- Dropdown Menu List -->
                  <div
                    v-if="isTeacherDropdownOpen"
                    class="dropdown-menu show w-100 mt-1 shadow-sm border p-1"
                    style="position: absolute; top: 100%; left: 0; z-index: 1050; max-height: 250px; overflow-y: auto;"
                  >
                    <!-- Opsi: Semua Guru -->
                    <div
                      class="dropdown-item rounded-2 py-2 px-3 d-flex align-items-center justify-content-between"
                      :class="{ 'bg-primary text-white': !selectedTeacherId }"
                      @click="selectTeacher(null)"
                      style="cursor: pointer;"
                    >
                      <span class="fs-13 d-inline-flex align-items-center">
                        <img src="@/assets/img/icon/users.svg" alt="All" width="16" height="16" class="me-2" />
                        Semua Guru (Teacher)
                      </span>
                      <img
                        v-if="!selectedTeacherId"
                        src="@/assets/img/icon/check-circle.svg"
                        alt="Selected"
                        width="14"
                        height="14"
                      />
                    </div>

                    <div class="dropdown-divider my-1"></div>

                    <!-- Opsi: Daftar Guru Berdasarkan Ketikan -->
                    <div
                      v-for="teacher in filteredTeacherOptions"
                      :key="teacher.userID"
                      class="dropdown-item rounded-2 py-2 px-3 d-flex align-items-center justify-content-between"
                      :class="{ 'bg-primary text-white': String(selectedTeacherId) === String(teacher.userID) }"
                      @click="selectTeacher(teacher)"
                      style="cursor: pointer;"
                    >
                      <div class="d-flex align-items-center text-truncate me-2">
                        <img src="@/assets/img/icon/teacher.svg" alt="Teacher" width="16" height="16" class="me-2" />
                        <span class="text-truncate fs-13">{{ teacher.name }}</span>
                      </div>
                      <img
                        v-if="String(selectedTeacherId) === String(teacher.userID)"
                        src="@/assets/img/icon/check-circle.svg"
                        alt="Selected"
                        width="14"
                        height="14"
                      />
                    </div>

                    <!-- Kosong jika pencarian tidak ditemukan -->
                    <div
                      v-if="filteredTeacherOptions.length === 0"
                      class="text-muted text-center py-3 fs-13"
                    >
                      <img src="@/assets/img/icon/search-icon.svg" alt="" width="20" height="20" class="d-block mx-auto mb-1 opacity-50" />
                      Guru tidak ditemukan
                    </div>
                  </div>
                </div>

                <!-- Reset Filter -->
                <div class="col-md-2 col-sm-4 col-12 text-end">
                  <button
                    class="btn btn-outline-secondary w-100"
                    :disabled="!searchQuery && !selectedTeacherId && !teacherSearchQuery"
                    @click="resetFilters"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State (No paths overall) -->
          <div
            v-if="learningPathStore.learningPaths.length === 0 && !sharedStore.loading"
            class="text-center py-5 card border-0 shadow-sm"
          >
            <div class="card-body py-5">
              <img
                src="@/assets/img/icon/career-01.svg"
                alt="Empty"
                width="64"
                height="64"
                class="d-block mx-auto mb-3 opacity-75"
              />
              <h5 class="text-muted fw-bold">Belum Ada Learning Path</h5>
              <p class="text-muted fs-14 mb-0">
                Saat ini belum ada Guru/Teacher yang membuat Learning Path di sistem.
              </p>
            </div>
          </div>

          <!-- Empty State (Filter result empty) -->
          <div
            v-else-if="filteredPaths.length === 0 && !sharedStore.loading"
            class="text-center py-5 card border-0 shadow-sm"
          >
            <div class="card-body py-5">
              <img
                src="@/assets/img/icon/search-icon.svg"
                alt="Empty Search"
                width="64"
                height="64"
                class="d-block mx-auto mb-3 opacity-50"
              />
              <h5 class="text-muted fw-bold">Tidak Ada Hasil yang Cocok</h5>
              <p class="text-muted fs-14 mb-3">
                Tidak ada Learning Path yang sesuai dengan pencarian atau filter yang dipilih.
              </p>
              <button class="btn btn-sm btn-primary" @click="resetFilters">
                Reset Filter
              </button>
            </div>
          </div>

          <!-- Learning Path Cards -->
          <div v-else class="d-flex flex-column gap-3">
            <div
              v-for="path in filteredPaths"
              :key="path.id"
              class="card border-0 shadow-sm"
            >
              <div class="card-body">
                <!-- Header Card -->
                <div class="d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2">
                  <div>
                    <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                      <h5 class="fw-bold mb-0 text-dark">{{ path.title }}</h5>
                      <span class="badge bg-primary-subtle text-primary border px-2 py-1 fs-12 fw-medium d-inline-flex align-items-center">
                        <img src="@/assets/img/icon/teacher.svg" alt="" width="14" height="14" class="me-1" />
                        {{ path.teacher_name || 'Guru' }}
                      </span>
                    </div>
                    <p class="text-muted fs-13 mb-0" v-if="path.note">
                      {{ path.note }}
                    </p>
                  </div>

                  <div class="d-flex align-items-center gap-2">
                    <span class="text-muted fs-12 me-1 d-inline-flex align-items-center" v-if="path.created_at">
                      <img src="@/assets/img/icon/time.svg" alt="" width="13" height="13" class="me-1" />
                      {{ formatDate(path.created_at) }}
                    </span>
                    <button
                      class="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                      @click="openDetail(path)"
                    >
                      <i class="fa-solid fa-eye me-1"></i> Detail
                    </button>
                  </div>
                </div>

                <!-- Course Items List -->
                <div class="border rounded-2 p-3 bg-light-subtle mb-3">
                  <p class="fw-medium fs-13 text-muted mb-2 d-inline-flex align-items-center">
                    <img src="@/assets/img/icon/book.svg" alt="" width="16" height="16" class="me-1" />
                    Course dalam Path ({{ path.item_count || (path.items ? path.items.length : 0) }})
                  </p>
                  <div
                    v-for="item in path.items"
                    :key="item.id"
                    class="d-flex align-items-center py-2 border-bottom last-border-0"
                  >
                    <span
                      class="badge bg-primary text-white me-3 fw-bold rounded-circle d-flex align-items-center justify-content-center"
                      style="width: 26px; height: 26px; font-size: 11px;"
                    >
                      {{ item.order }}
                    </span>
                    <router-link
                      :to="{ name: 'CourseDetail', params: { course_id: item.course } }"
                      class="fs-14 text-dark text-decoration-none fw-medium d-inline-flex align-items-center"
                    >
                      <span>{{ item.course_title }}</span>
                      <i class="fa-solid fa-arrow-right fs-12 ms-2 text-muted"></i>
                    </router-link>
                  </div>
                  <div v-if="!path.items || path.items.length === 0" class="text-muted fs-13 text-center py-2">
                    Belum ada course ditambahkan pada path ini.
                  </div>
                </div>

                <!-- Enrolled Students List -->
                <div>
                  <p class="fw-medium fs-13 text-muted mb-2 d-inline-flex align-items-center">
                    <img src="@/assets/img/icon/student.svg" alt="" width="16" height="16" class="me-1" />
                    Students Terdaftar ({{ path.student_count || 0 }})
                  </p>
                  <div
                    v-if="path.students && path.students.length > 0"
                    class="d-flex flex-wrap gap-2"
                  >
                    <span
                      v-for="student in path.students"
                      :key="student.student_id"
                      class="badge bg-light text-dark fs-12 fw-normal py-1 px-2 border d-inline-flex align-items-center gap-1"
                    >
                      <img src="@/assets/img/icon/student.svg" alt="" width="12" height="12" class="me-1 opacity-75" />
                      <span>{{ student.name }}</span>
                    </span>
                  </div>
                  <div v-else class="text-muted fs-13 fst-italic">
                    Belum ada siswa yang di-assign ke learning path ini.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- /Learning Path Cards -->
        </div>
      </div>
    </div>
  </div>

  <!-- ======================== -->
  <!-- Modal: Detail Path       -->
  <!-- ======================== -->
  <div
    class="modal fade"
    :class="{ show: showDetailModal }"
    :style="{ display: showDetailModal ? 'block' : 'none' }"
    tabindex="-1"
    v-if="selectedPath"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title fw-bold mb-0">{{ selectedPath.title }}</h5>
            <span class="badge bg-primary-subtle text-primary border mt-1 d-inline-flex align-items-center">
              <img src="@/assets/img/icon/teacher.svg" alt="" width="14" height="14" class="me-1" />
              Guru: {{ selectedPath.teacher_name || 'Teacher' }}
            </span>
          </div>
          <button type="button" class="btn-close" @click="closeDetail"></button>
        </div>

        <div class="modal-body">
          <!-- Description -->
          <div class="mb-4" v-if="selectedPath.note">
            <h6 class="fw-bold fs-13 text-muted text-uppercase mb-1">Catatan / Deskripsi</h6>
            <p class="fs-14 text-dark bg-light p-3 rounded-2 mb-0">
              {{ selectedPath.note }}
            </p>
          </div>

          <!-- Courses list step by step -->
          <div class="mb-4">
            <h6 class="fw-bold fs-13 text-muted text-uppercase mb-2">
              Urutan Course Pembelajaran ({{ selectedPath.item_count || 0 }})
            </h6>
            <div class="border rounded-2 overflow-hidden">
              <div
                v-for="item in selectedPath.items"
                :key="item.id"
                class="d-flex align-items-center justify-content-between p-3 border-bottom"
              >
                <div class="d-flex align-items-center">
                  <span
                    class="badge bg-primary text-white me-3 fw-bold rounded-circle d-flex align-items-center justify-content-center"
                    style="width: 28px; height: 28px;"
                  >
                    {{ item.order }}
                  </span>
                  <div>
                    <h6 class="mb-0 fs-14 fw-semibold text-dark">{{ item.course_title }}</h6>
                    <small class="text-muted">Langkah ke-{{ item.order }} dalam tahapan pembelajaran</small>
                  </div>
                </div>
                <router-link
                  :to="{ name: 'CourseDetail', params: { course_id: item.course } }"
                  class="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                  target="_blank"
                >
                  <span>Lihat Course</span>
                  <i class="fa-solid fa-arrow-right ms-2 fs-12"></i>
                </router-link>
              </div>
              <div
                v-if="!selectedPath.items || selectedPath.items.length === 0"
                class="text-center text-muted py-3 fs-14"
              >
                Belum ada course di dalam path ini.
              </div>
            </div>
          </div>

          <!-- Assigned students -->
          <div>
            <h6 class="fw-bold fs-13 text-muted text-uppercase mb-2">
              Daftar Siswa Terdaftar ({{ selectedPath.student_count || 0 }})
            </h6>
            <div class="border rounded-2 p-3 bg-light" style="max-height: 250px; overflow-y: auto;">
              <div
                v-if="selectedPath.students && selectedPath.students.length > 0"
                class="row g-2"
              >
                <div
                  v-for="student in selectedPath.students"
                  :key="student.student_id"
                  class="col-md-6 col-12"
                >
                  <div class="bg-white border rounded-2 p-2 d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                        style="width: 32px; height: 32px; background: #e8eeff;"
                      >
                        <img src="@/assets/img/icon/student.svg" alt="" width="16" height="16" />
                      </div>
                      <span class="fs-13 fw-medium text-dark">{{ student.name }}</span>
                    </div>
                    <small class="text-muted fs-11" v-if="student.enrolled_at">
                      {{ formatDate(student.enrolled_at) }}
                    </small>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted py-3 fs-14">
                Belum ada siswa yang di-assign ke path ini.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeDetail">
            Tutup
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="showDetailModal"></div>

  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
</template>

<style scoped>
.last-border-0:last-child {
  border-bottom: 0 !important;
}
</style>
